import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Bell, CheckCircle2, CircleCheck, CircleDashed, Download, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  api, displayName, errorMessage, fmtDate, fmtDay, fmtInt, fmtMoney, fmtPct, humanize,
  invalidateQueries, isoDay, parseDate, pctOf, PLAN_LABEL, PLAN_TONE, useQuery, useUsers,
} from './lib';
import {
  Btn, DateRangeControl, EmptyState, ErrorState, Notice, Page, Pagination, Panel, Pill,
  RefreshButton, RowsSkeleton, StatCard, StatGrid, StatSkeletons, Table, TD, TextInput, TH, TR,
  usePagination, useDateRange,
} from './ui';
import { ChartTooltip, COLORS, cursorFill, gridProps, legendProps, xAxisProps, yAxisProps } from './charts';
import OnboardingFunnel from './OnboardingFunnel';

const rangeParams = (r) => `start=${r.start}&end=${r.end}&days=${r.days}`;

function useKpi(name, range, path, opts) {
  return useQuery(`kpi:${name}:${range.start}:${range.end}`, () => api.get(`${path}?${rangeParams(range)}`), { maxAge: 5 * 60_000, ...opts });
}

function TabState({ query, children, stats = 4 }) {
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />;
  if (!query.data) return <div className="space-y-4"><StatSkeletons count={stats} /><RowsSkeleton rows={4} className="rounded-xl border border-border" /></div>;
  return <div className={cn('space-y-4', query.loading && 'opacity-70 transition-opacity')}>{children}</div>;
}

/* ── Installs ────────────────────────────────────────────────────────────── */

function ProviderRow({ name, provider }) {
  const ok = provider?.available;
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      {ok ? <CircleCheck className="mt-0.5 h-4 w-4 text-emerald-600" /> : <CircleDashed className="mt-0.5 h-4 w-4 text-muted-foreground" />}
      <div className="min-w-0 text-sm">
        <p className="font-medium text-foreground">{name}</p>
        <p className="mt-0.5 break-words text-xs text-muted-foreground">
          {ok ? `${fmtInt(provider.total_new)} downloads reported` : provider?.reason || 'Not connected'}
          {ok && provider.latest_date && ` · data through ${fmtDay(provider.latest_date)}`}
          {!ok && provider?.setup_url && (
            <> · <a href={provider.setup_url} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2">Set up</a></>
          )}
        </p>
      </div>
    </div>
  );
}

function InstallsTab({ range }) {
  const q = useKpi('installs', range, '/admin/kpi/installs');
  const [showDevices, setShowDevices] = useState(false);
  const d = q.data || {};
  const t = d.totals || {};
  const storeTrend = d.store_trend || [];
  const hasStore = storeTrend.some((x) => x.total > 0);
  const regBase = t.store_installs > 0 ? t.store_installs : t.proxy_installs;
  const devices = d.no_account_devices || [];

  return (
    <TabState query={q}>
      <StatGrid>
        <StatCard
          label="Store downloads"
          value={t.store_available ? fmtInt(t.store_installs) : '—'}
          hint={t.store_available ? `${fmtInt(t.apple_installs)} App Store · ${fmtInt(t.play_installs)} Google Play` : 'No store connected yet'}
        />
        <StatCard label="Accounts created" value={fmtInt(t.new_accounts)} hint={`${fmtInt(t.total_accounts_all_time)} all time`} />
        <StatCard
          label={t.store_installs > 0 ? 'Download → account' : 'App open → account'}
          value={fmtPct(pctOf(t.new_accounts, regBase))}
          hint={t.store_installs > 0 ? 'Accounts per store download' : 'Estimate from app opens'}
        />
        <StatCard
          label="Opened, never signed up"
          value={fmtInt(t.no_account)}
          hint="Devices that opened the app without an account"
          tone={t.no_account > 0 ? 'warning' : undefined}
        />
      </StatGrid>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          title="Store downloads"
          description="Real downloads reported by Apple and Google Play"
          className="lg:col-span-2"
        >
          {hasStore ? (
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={storeTrend} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
                  <YAxis {...yAxisProps} />
                  <Tooltip cursor={cursorFill} content={<ChartTooltip />} />
                  <Legend {...legendProps} />
                  <Bar dataKey="apple" name="App Store" stackId="s" fill={COLORS.ink} maxBarSize={18} />
                  <Bar dataKey="play" name="Google Play" stackId="s" fill={COLORS.ink2} radius={[3, 3, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState
              icon={Download}
              title="No store downloads reported for this period"
              description="Store reports arrive late: Apple's by a day or two, Google Play's by a week or more (see Sources)."
            />
          )}
        </Panel>
        <Panel title="Sources" flush>
          <div className="divide-y divide-border">
            <ProviderRow name="App Store Connect" provider={d.apple_installs} />
            <ProviderRow name="Google Play Console" provider={d.play_installs} />
          </div>
        </Panel>
      </div>

      <Panel title="App opens and new accounts" description="First app open per device (an estimate of installs) next to accounts created, per day">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.trend || []} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} barGap={2}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip cursor={cursorFill} content={<ChartTooltip />} />
              <Legend {...legendProps} />
              <Bar dataKey="proxy_installs" name="First app opens" fill={COLORS.faint} radius={[3, 3, 0, 0]} maxBarSize={12} />
              <Bar dataKey="new_accounts" name="New accounts" fill={COLORS.ink} radius={[3, 3, 0, 0]} maxBarSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {devices.length > 0 && (
        <Panel
          title="Devices that never signed up"
          description="Anonymous device IDs that opened the app but never created an account"
          actions={<Btn variant="ghost" size="sm" onClick={() => setShowDevices((s) => !s)}>{showDevices ? 'Hide' : `Show ${fmtInt(devices.length)}`}</Btn>}
          bodyClassName={showDevices ? undefined : 'hidden'}
        >
          <div className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
            {devices.map((id) => (
              <code key={id} className="truncate rounded bg-secondary/70 px-2 py-1 text-[11px] text-muted-foreground">{id}</code>
            ))}
          </div>
        </Panel>
      )}
    </TabState>
  );
}

/* ── Retention ───────────────────────────────────────────────────────────── */

/* Weighted retention over the cohorts old enough to have reached day N. */
function retentionAt(cohorts, n, key) {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - n);
  const mature = cohorts.filter((c) => parseDate(c.install_date) <= cutoff);
  const size = mature.reduce((s, c) => s + (c.cohort_size || 0), 0);
  const kept = mature.reduce((s, c) => s + (c[key] || 0), 0);
  return { pct: size ? (kept / size) * 100 : null, size, cohorts: mature.length };
}

function cellPct(c, n, key) {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - n);
  if (parseDate(c.install_date) > cutoff || !c.cohort_size) return null;
  return ((c[key] || 0) / c.cohort_size) * 100;
}

function RetentionTab({ range }) {
  const q = useQuery('kpi:retention:400', () => api.get('/admin/kpi/retention?days=400'), { maxAge: 5 * 60_000 });
  const cohorts = useMemo(
    () => (q.data?.cohorts || []).filter((c) => c.install_date >= range.start && c.install_date <= range.end),
    [q.data, range.start, range.end],
  );
  const d1 = retentionAt(cohorts, 1, 'd1_retained');
  const d7 = retentionAt(cohorts, 7, 'd7_retained');
  const d30 = retentionAt(cohorts, 30, 'd30_retained');
  const pager = usePagination(cohorts, 15, `${range.start}|${range.end}`);

  const stat = (label, r, target) => (
    <StatCard
      label={label}
      value={fmtPct(r.pct)}
      hint={r.cohorts ? `${fmtInt(r.size)} people · target ${target}%` : 'No cohort is old enough yet'}
      tone={r.pct != null && r.pct < target ? 'warning' : undefined}
    />
  );
  const cell = (v, target) => (v == null
    ? <span className="text-muted-foreground/50">—</span>
    : <span className={cn(v < target && 'text-amber-700')}>{fmtPct(v, 0)}</span>);

  return (
    <TabState query={q} stats={3}>
      <StatGrid className="lg:grid-cols-3">
        {stat('Day 1 retention', d1, 40)}
        {stat('Day 7 retention', d7, 25)}
        {stat('Day 30 retention', d30, 15)}
      </StatGrid>
      <Panel
        flush
        title="Cohorts"
        description="People grouped by their first active day, and how many came back exactly 1, 7 and 30 days later. Cohorts too young for a column show a dash."
      >
        {cohorts.length === 0 ? (
          <EmptyState
            title="No cohorts in this period"
            description="Cohorts are built from the daily activity table by the nightly aggregation. If this stays empty, run the aggregation from the Alerts tab."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <TH>First active day</TH>
                  <TH align="right">People</TH>
                  <TH align="right">Day 1</TH>
                  <TH align="right">Day 7</TH>
                  <TH align="right">Day 30</TH>
                </tr>
              </thead>
              <tbody>
                {pager.slice.map((c) => (
                  <TR key={c.install_date}>
                    <TD className="whitespace-nowrap">{fmtDate(c.install_date)}</TD>
                    <TD align="right">{fmtInt(c.cohort_size)}</TD>
                    <TD align="right">{cell(cellPct(c, 1, 'd1_retained'), 40)}</TD>
                    <TD align="right">{cell(cellPct(c, 7, 'd7_retained'), 25)}</TD>
                    <TD align="right">{cell(cellPct(c, 30, 'd30_retained'), 15)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            <Pagination {...pager} noun="cohorts" />
          </>
        )}
      </Panel>
    </TabState>
  );
}

/* ── Monetization ────────────────────────────────────────────────────────── */

function MonetizationTab({ range }) {
  const conv = useQuery(
    `conversions:${range.start}:${range.end}`,
    () => api.get(`/admin/conversions?start=${range.start}&end=${range.end}`),
  );
  const funnel = useKpi('funnel', range, '/admin/kpi/funnel');
  const c = conv.data;
  const f = funnel.data?.funnel || {};
  const m = funnel.data?.monetization || {};

  return (
    <TabState query={conv}>
      <StatGrid>
        <StatCard label="Signed up" value={fmtInt(c?.new_users)} hint="Customers, staff excluded" />
        <StatCard
          label="Bought Premium"
          value={fmtInt(c?.paid)}
          hint={c ? `${fmtPct(c.signup_to_paid_pct)} of signups · ${fmtPct(c.onboarded_to_paid_pct)} of onboarded` : undefined}
        />
        <StatCard
          label="Revenue in period"
          value={funnel.data ? fmtMoney(m.total_revenue_period, 'EUR') : '—'}
          hint="All currencies added together"
        />
        <StatCard label="Premium customers" value={fmtInt(m.total_premium_users)} hint="All time, including staff" />
      </StatGrid>

      <Panel
        title="Paywall events"
        description="From analytics events. Paywall views are only sent by the dashboard paywall, and purchases only by web checkout, so app-store purchases are missing here; the numbers above come from the database and are complete."
      >
        {funnel.error ? (
          <ErrorState error={funnel.error} onRetry={funnel.refetch} />
        ) : (
          <KeyValue3 items={[
            { label: 'Paywall views', value: fmtInt(f.paywall_views), hint: `${fmtInt(f.unique_paywall_users)} people` },
            { label: 'Web purchases', value: fmtInt(f.subscriptions_started), hint: f.unique_paywall_users ? `${fmtPct(pctOf(f.subscriptions_started, f.unique_paywall_users))} of viewers` : undefined },
            { label: 'Trials started', value: fmtInt(f.trial_starts), hint: 'Legacy: trials were retired with lifetime' },
          ]} />
        )}
      </Panel>
    </TabState>
  );
}

function KeyValue3({ items }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {items.map((i) => (
        <div key={i.label}>
          <p className="text-xs text-muted-foreground">{i.label}</p>
          <p className="mt-1 text-xl font-semibold tabular">{i.value}</p>
          {i.hint && <p className="mt-0.5 text-xs text-muted-foreground">{i.hint}</p>}
        </div>
      ))}
    </div>
  );
}

/* ── Engagement ──────────────────────────────────────────────────────────── */

function EngagementTab({ range, onOpenCustomer }) {
  const trendQ = useKpi('trend', range, '/admin/kpi/daily-trend');
  const wru = useQuery('kpi:wru', () => api.get('/admin/kpi/wru'), { maxAge: 5 * 60_000 });
  const { data: users } = useUsers();
  const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u])), [users]);

  const trend = trendQ.data?.trend || [];
  const mau = trendQ.data?.mau || 0;
  const avgDau = trend.length ? trend.reduce((s, d) => s + d.dau, 0) / trend.length : 0;
  const done = trend.reduce((s, d) => s + d.done, 0);
  const skip = trend.reduce((s, d) => s + d.skip, 0);
  const wruUsers = wru.data?.users || [];

  return (
    <TabState query={trendQ}>
      <StatGrid className="lg:grid-cols-4">
        <StatCard label="Active people" value={fmtInt(mau)} hint="Opened the app or did a gesture in the period" />
        <StatCard label="Daily active, average" value={avgDau ? avgDau.toFixed(1) : '0'} hint={`Stickiness ${fmtPct(pctOf(avgDau, mau))}`} />
        <StatCard label="Gestures done" value={fmtInt(done)} hint={`${fmtInt(skip)} skipped · ${fmtPct(pctOf(skip, done + skip))} skip rate`} tone={pctOf(skip, done + skip) > 40 ? 'warning' : undefined} />
        <StatCard label="Reliable this week" value={fmtInt(wru.data?.wru_count)} hint="3 or more gestures done in the last 7 days" />
      </StatGrid>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Daily active people">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="kpiDau" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.ink} stopOpacity={0.14} />
                    <stop offset="100%" stopColor={COLORS.ink} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
                <YAxis {...yAxisProps} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="dau" name="Active" stroke={COLORS.ink} strokeWidth={2} fill="url(#kpiDau)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Gestures done and skipped">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" tickFormatter={fmtDay} {...xAxisProps} />
                <YAxis {...yAxisProps} />
                <Tooltip cursor={cursorFill} content={<ChartTooltip />} />
                <Legend {...legendProps} />
                <Bar dataKey="done" name="Done" stackId="a" fill={COLORS.ink} maxBarSize={16} />
                <Bar dataKey="skip" name="Skipped" stackId="a" fill={COLORS.ink2} radius={[3, 3, 0, 0]} maxBarSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <Panel flush title="Reliable this week" description="People with 3 or more gestures done in the last 7 days">
        {wru.error ? (
          <div className="p-4"><ErrorState error={wru.error} onRetry={wru.refetch} /></div>
        ) : !wru.data ? (
          <RowsSkeleton rows={4} />
        ) : wruUsers.length === 0 ? (
          <EmptyState title="Nobody yet this week" />
        ) : (
          <Table>
            <thead><tr><TH>Customer</TH><TH>Plan</TH><TH align="right">Done in 7 days</TH></tr></thead>
            <tbody>
              {wruUsers.map((w) => {
                const u = userById[w.user_id];
                const plan = w.subscription_status || u?.subscription_status || 'free';
                return (
                  <TR key={w.user_id} onClick={u ? () => onOpenCustomer(w.user_id) : undefined}>
                    <TD>
                      <p className="truncate text-sm text-foreground">{u ? displayName(u) : w.email || w.user_id}</p>
                      {u?.email && <p className="truncate text-xs text-muted-foreground">{u.email}</p>}
                    </TD>
                    <TD><Pill tone={PLAN_TONE[plan]}>{PLAN_LABEL[plan] || humanize(plan)}</Pill></TD>
                    <TD align="right">{fmtInt(w.done_last_7d)}</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </Panel>
    </TabState>
  );
}

/* ── Alerts ──────────────────────────────────────────────────────────────── */

const THRESHOLDS = [
  ['Weekly reliable users drop more than 15% week over week', 'wru_drop'],
  ['Day 7 retention below 25%', 'd7_retention_low'],
  ['Paywall → trial below 40% (legacy)', 'trial_conversion_low'],
  ['More than 25% of done gestures marked “already doing this”', 'internalized_ratio_high'],
];

function AlertsTab() {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return isoDay(d);
  });
  const [aggregating, setAggregating] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      setResult(await api.post('/admin/kpi/check-alerts'));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const aggregate = async () => {
    setAggregating(true);
    try {
      const res = await api.post('/admin/kpi/aggregate', { date });
      toast.success(`Daily activity rebuilt for ${fmtDate(res.aggregated_for || date)}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setAggregating(false);
    }
  };

  const fired = new Map((result?.details || []).map((a) => [a.alert, a.value]));

  return (
    <div className="space-y-4">
      <Panel
        title="Alert check"
        description="Checks these thresholds now. Alerts that fire are also posted to Slack when SLACK_KPI_WEBHOOK_URL is set on the server."
        actions={(
          <Btn size="sm" onClick={run} disabled={running}>
            <Bell className="h-3.5 w-3.5" />{running ? 'Checking…' : 'Run check'}
          </Btn>
        )}
        flush
      >
        <ul className="divide-y divide-border">
          {THRESHOLDS.map(([label, key]) => {
            const hit = fired.has(key);
            return (
              <li key={key} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <span className="text-foreground">{label}</span>
                {!result ? <Pill>Not checked</Pill> : hit
                  ? <Pill tone="danger">Fired · {Number(fired.get(key)).toFixed(1)}</Pill>
                  : <Pill tone="success">OK</Pill>}
              </li>
            );
          })}
        </ul>
        {result && result.alerts_fired === 0 && (
          <div className="flex items-center gap-2 border-t border-border px-4 py-3 text-xs text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> All clear: no alert fired.
          </div>
        )}
      </Panel>

      <Panel
        title="Rebuild daily activity"
        description="Retention cohorts and weekly reliable users are read from the daily activity table, which a nightly job fills from the raw events. If a day looks missing, rebuild it here."
      >
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Day</span>
            <TextInput type="date" value={date} max={isoDay(new Date())} onChange={(e) => setDate(e.target.value)} className="w-[170px]" />
          </label>
          <Btn variant="outline" onClick={aggregate} disabled={aggregating || !date}>
            {aggregating ? 'Rebuilding…' : 'Rebuild'}
          </Btn>
        </div>
      </Panel>
    </div>
  );
}

/* ── Panel ───────────────────────────────────────────────────────────────── */

const TABS = [
  ['installs', 'Installs'],
  ['onboarding', 'Onboarding'],
  ['retention', 'Retention'],
  ['monetization', 'Monetization'],
  ['engagement', 'Engagement'],
  ['alerts', 'Alerts'],
];

export default function KpiPanel({ onOpenCustomer }) {
  const range = useDateRange('30d');
  const [tab, setTab] = useState('installs');
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Page
      title="KPIs"
      description="Installs, onboarding, retention, monetization and engagement for the selected period. Staff accounts are included unless a number says otherwise."
      actions={tab !== 'alerts' && <DateRangeControl range={range} />}
    >
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-3 overflow-x-auto">
          <TabsList className="h-9 rounded-lg">
            {TABS.map(([value, label]) => (
              <TabsTrigger key={value} value={value} className="rounded-md text-[13px] shadow-none data-[state=active]:shadow-none">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tab !== 'alerts' && (
            <RefreshButton
              onClick={() => {
                invalidateQueries('kpi:');
                invalidateQueries('conversions:');
                setRefreshKey((k) => k + 1); // remount the tab so it reloads
              }}
            />
          )}
        </div>
        <div className="mt-4" key={refreshKey}>
          <TabsContent value="installs" className="mt-0">{tab === 'installs' && <InstallsTab range={range} />}</TabsContent>
          <TabsContent value="onboarding" className="mt-0">{tab === 'onboarding' && <OnboardingFunnel range={range} onOpenCustomer={onOpenCustomer} />}</TabsContent>
          <TabsContent value="retention" className="mt-0">{tab === 'retention' && <RetentionTab range={range} />}</TabsContent>
          <TabsContent value="monetization" className="mt-0">{tab === 'monetization' && <MonetizationTab range={range} />}</TabsContent>
          <TabsContent value="engagement" className="mt-0">{tab === 'engagement' && <EngagementTab range={range} onOpenCustomer={onOpenCustomer} />}</TabsContent>
          <TabsContent value="alerts" className="mt-0">{tab === 'alerts' && <AlertsTab />}</TabsContent>
        </div>
      </Tabs>
      <Notice tone="neutral" icon={Smartphone} title="About these numbers">
        Installs come from the stores; everything else comes from the app’s own analytics events and the database. Dates are in UTC.
      </Notice>
    </Page>
  );
}
